import { widgetSystemConfig } from "../../config/widget-system.config";
import { WidgetDataOptions } from "../../types/widget-systems";
import { widgetLogger } from "./WidgetLogger";
import { widgetMetrics } from "./WidgetMetrics";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  error?: Error;
}

class WidgetDataManager {
  private static instance: WidgetDataManager;
  private cache: Map<string, CacheEntry<any>> = new Map();
  private refreshIntervals: Map<string, NodeJS.Timeout> = new Map();
  private defaultCacheTimeout = widgetSystemConfig.defaultCacheTimeout;
  private defaultRefreshInterval = widgetSystemConfig.defaultRefreshInterval;

  private constructor() {}

  static getInstance(): WidgetDataManager {
    if (!WidgetDataManager.instance) {
      WidgetDataManager.instance = new WidgetDataManager();
    }
    return WidgetDataManager.instance;
  }

  private getCacheKey(widgetId: string, dataKey: string): string {
    return `${widgetId}:${dataKey}`;
  }

  async getData<T>(
    widgetId: string,
    dataKey: string,
    fetchFn: () => Promise<T>,
    options: WidgetDataOptions = {}
  ): Promise<T> {
    const startTime = performance.now();
    const cacheKey = this.getCacheKey(widgetId, dataKey);
    const cacheEntry = this.cache.get(cacheKey);
    const now = Date.now();

    widgetMetrics.recordTotal(widgetId);

    if (
      cacheEntry &&
      now - cacheEntry.timestamp <
        (options.cacheTimeout || this.defaultCacheTimeout)
    ) {
      if (cacheEntry.error) {
        widgetMetrics.recordError(widgetId);
        throw cacheEntry.error;
      }
      widgetMetrics.recordCacheHit(widgetId);
      return cacheEntry.data;
    }

    try {
      widgetMetrics.recordCacheMiss(widgetId);
      const data = await fetchFn();
      this.cache.set(cacheKey, {
        data,
        timestamp: now,
      });

      const loadTime = performance.now() - startTime;
      widgetMetrics.recordLoadTime(widgetId, loadTime);

      widgetLogger.info(widgetId, "Данные успешно получены", {
        dataKey,
        loadTime,
      });

      return data;
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.cache.set(cacheKey, {
        data: null,
        timestamp: now,
        error: errorObj,
      });

      widgetMetrics.recordError(widgetId);
      widgetLogger.error(widgetId, "Ошибка получения данных", {
        dataKey,
        error: errorObj,
      });

      options.onError?.(errorObj);
      throw errorObj;
    }
  }

  startAutoRefresh(
    widgetId: string,
    dataKey: string,
    fetchFn: () => Promise<any>,
    options: WidgetDataOptions = {}
  ): void {
    const interval = options.refreshInterval || this.defaultRefreshInterval;
    const cacheKey = this.getCacheKey(widgetId, dataKey);

    // Очищаем существующий интервал, если он есть
    this.stopAutoRefresh(widgetId, dataKey);

    const refreshInterval = setInterval(async () => {
      try {
        await this.getData(widgetId, dataKey, fetchFn, options);
        widgetMetrics.recordRefresh(widgetId);
      } catch (error) {
        console.error(`Ошибка автообновления для виджета ${widgetId}:`, error);
      }
    }, interval);

    this.refreshIntervals.set(cacheKey, refreshInterval);
    widgetLogger.info(widgetId, "Автообновление запущено", {
      dataKey,
      interval,
    });
  }

  stopAutoRefresh(widgetId: string, dataKey: string): void {
    const cacheKey = this.getCacheKey(widgetId, dataKey);
    const interval = this.refreshIntervals.get(cacheKey);
    if (interval) {
      clearInterval(interval);
      this.refreshIntervals.delete(cacheKey);
      widgetLogger.info(widgetId, "Автообновление остановлено", { dataKey });
    }
  }

  clearCache(widgetId?: string, dataKey?: string): void {
    if (widgetId && dataKey) {
      const cacheKey = this.getCacheKey(widgetId, dataKey);
      this.cache.delete(cacheKey);
      this.stopAutoRefresh(widgetId, dataKey);
      widgetLogger.info(widgetId, "Кэш очищен", { dataKey });
    } else if (widgetId) {
      // Очищаем все данные для виджета
      Array.from(this.cache.keys()).forEach((key) => {
        if (key.startsWith(`${widgetId}:`)) {
          this.cache.delete(key);
          const parts = key.split(":");
          const wid = parts[0];
          const dk = parts[1];
          if (wid !== undefined && dk !== undefined) this.stopAutoRefresh(wid, dk);
        }
      });
      widgetLogger.info(widgetId, "Кэш виджета полностью очищен");
    } else {
      // Очищаем весь кэш
      this.cache.clear();
      Array.from(this.refreshIntervals.values()).forEach((interval) => {
        clearInterval(interval);
      });
      this.refreshIntervals.clear();
      widgetLogger.info("system", "Кэш системы полностью очищен");
    }
  }
}

export const widgetDataManager = WidgetDataManager.getInstance();
